/**
 * StockMovementsPage - Histórico de movimentações (item a item) + botão Movimentar
 *
 * RELEASE: 2026-10-19 v1.5.0 - Destino Transportadora no histórico e filtros identificados sem truncamento.
 * RELEASE: 2026-10-09 v1.4.0 - Filtros rápidos no estilo pill padrão (QUICK_FILTER_PILL) e busca com lupa.
 * RELEASE: 2026-10-08 v1.3.0 - Reutiliza a tabela compartilhada com as fichas individuais.
 * RELEASE: 2026-10-08 v1.2.0 - Reconsulta ao abrir e oferece atualização manual preservando filtros.
 * RELEASE: 2026-10-08 v1.1.0 - Produto em etiquetas, item detalhado, data com relógio e observações.
 * RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 4 do Estoque).
 */
import { useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, RefreshCw, Search } from 'lucide-react';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import {
  ListPageLayout, ListPageHeader, ListPageFiltersBar, ListPageContent,
  LIST_FILTERS_INPUT_CLASS,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { ExportMenuButton } from '@/components/export/ExportMenuButton';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import {
  STOCK_DESTINATION_TYPES, useStockMovementsList, type StockMovementsFilters,
} from '@/hooks/useStockMovements';
import { StockMoveDialog } from '@/components/stock/StockMoveDialog';
import { StockMovementsTable } from '@/components/stock/StockMovementsTable';
import { cn } from '@/lib/utils';

export default function StockMovementsPage() {
  const { canCreate, isAdmin } = usePermissionsContext();
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<StockMovementsFilters>({
    search: '', itemType: 'all', destinationType: 'all', dateFrom: null, dateTo: null, page: 1, pageSize: 25,
  });
  const [moveOpen, setMoveOpen] = useState(false);
  const { data, isLoading, isFetching, refetch } = useStockMovementsList(filters);

  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchInput.trim() ? f : { ...f, search: searchInput.trim(), page: 1 })), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const allowMove = isAdmin || canCreate('stock_movements');
  const items = data?.items ?? [];
  const exportParams = useMemo(() => {
    const { page: _p, pageSize: _s, ...rest } = filters;
    return rest;
  }, [filters]);

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Movimentações"
        /* Busca premium centralizada na linha do título (padrão Retenção/TaskList).
           Oculta em mobile — nesse caso a busca fica na barra de filtros.
           `w-full` garante a largura total do slot central (mesmo tamanho da listagem de Negócios);
           subtítulo removido do cabeçalho. */
        center={
          <div className="relative w-full max-w-md mx-4 hidden md:block">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
            <Input
              placeholder="Buscar IMEI, ICCID, kit ou placa..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              maxLength={60}
              className="h-10 pl-10 pr-4 text-sm rounded-xl border-none bg-muted/40 hover:bg-muted/60 transition-all duration-200 focus-visible:bg-background focus-visible:shadow-md focus-visible:ring-1 focus-visible:ring-primary/20 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50"
            />
          </div>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => refetch()}
              disabled={isFetching}
              title="Atualizar movimentações"
              aria-label="Atualizar movimentações"
            >
              <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            </Button>
            <ExportMenuButton sourceId="stock_movements" params={exportParams} variant="icon" />
            {allowMove && (
              <Button size="xs" onClick={() => setMoveOpen(true)}>
                <ArrowRightLeft className="mr-1.5 h-4 w-4" /> Movimentar
              </Button>
            )}
          </div>
        }
      />
      <ListPageFiltersBar isFetching={isFetching}>
        {/* Busca com lupa — apenas mobile; no desktop fica centralizada no cabeçalho */}
        <div className="relative min-w-0 flex-1 sm:max-w-xs md:hidden">
          <Search className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground sm:h-3.5 sm:w-3.5" />
          <Input className={`${LIST_FILTERS_INPUT_CLASS} w-full pl-8`} value={searchInput} maxLength={60}
          placeholder="Buscar IMEI, ICCID, kit ou placa..." onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        {/* Largura pelo conteúdo: mantém o nome do filtro e a seleção legíveis. */}
        <Select value={filters.itemType} onValueChange={(v) => setFilters((f) => ({ ...f, itemType: v as StockMovementsFilters['itemType'], page: 1 }))}>
          <SelectTrigger aria-label="Item" className={cn(LIST_FILTERS_INPUT_CLASS, QUICK_FILTER_PILL, 'w-auto shrink-0 gap-2 whitespace-nowrap [&>span]:line-clamp-none')}>
            <span className="flex items-center gap-1"><span className="text-muted-foreground">Item:</span><SelectValue /></span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os itens</SelectItem>
            <SelectItem value="kit">Kits</SelectItem>
            <SelectItem value="tracker">Rastreadores</SelectItem>
            <SelectItem value="chip">Chips</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.destinationType} onValueChange={(v) => setFilters((f) => ({ ...f, destinationType: v as StockMovementsFilters['destinationType'], page: 1 }))}>
          <SelectTrigger aria-label="Destino" className={cn(LIST_FILTERS_INPUT_CLASS, QUICK_FILTER_PILL, 'w-auto shrink-0 gap-2 whitespace-nowrap [&>span]:line-clamp-none')}>
            <span className="flex items-center gap-1"><span className="text-muted-foreground">Destino:</span><SelectValue /></span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os destinos</SelectItem>
            {STOCK_DESTINATION_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
            {/* Transportadora é filtro do histórico; não libera movimentação manual para ela. */}
            <SelectItem value="carrier">Transportadora</SelectItem>
          </SelectContent>
        </Select>
        {/* Rótulos visíveis identificam o intervalo sem depender do calendário aberto. */}
        <label className="flex shrink-0 items-center gap-1 text-[11px] sm:text-sm">
          <span className="text-muted-foreground">De:</span>
          <Input type="date" aria-label="De" className={`${LIST_FILTERS_INPUT_CLASS} ${QUICK_FILTER_PILL} w-36 shrink-0`} value={filters.dateFrom ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value || null, page: 1 }))} />
        </label>
        <label className="flex shrink-0 items-center gap-1 text-[11px] sm:text-sm">
          <span className="text-muted-foreground">Até:</span>
          <Input type="date" aria-label="Até" className={`${LIST_FILTERS_INPUT_CLASS} ${QUICK_FILTER_PILL} w-36 shrink-0`} value={filters.dateTo ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value || null, page: 1 }))} />
        </label>
      </ListPageFiltersBar>
      <ListPageContent fullBleed>
        <StockMovementsTable items={items} isLoading={isLoading} />
        <PaginationFooter totalItems={data?.total ?? 0} currentPage={filters.page} pageSize={filters.pageSize}
          onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          onPageSizeChange={(pageSize) => setFilters((f) => ({ ...f, pageSize, page: 1 }))}
          pageSizeOptions={[10, 25, 50, 100]} />
      </ListPageContent>
      <StockMoveDialog open={moveOpen} onOpenChange={setMoveOpen} />
    </ListPageLayout>
  );
}
